import { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { format } from 'date-fns';
import {
  Box,
  VStack,
  Textarea,
  Button,
  Text,
  Flex,
  Heading,
  useColorModeValue,
  IconButton,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FiSave, FiTrash2 } from 'react-icons/fi';
import {
  currentDiaryAtom,
  fetchDiaryByDateAtom,
  upsertDiaryAtom,
  deleteDiaryAtom,
  isDiaryLoadingAtom,
} from '../../../atoms/v0/diaryAtoms';
import { Diary } from '../../../types/diary';

interface DiaryEditorProps {
  date: Date;
}

export const DiaryEditor: React.FC<DiaryEditorProps> = ({ date }) => {
  const { t } = useTranslation(['v0/calendar', 'v0/common']);
  const toast = useToast();
  const [content, setContent] = useState('');
  const [currentDiary] = useAtom(currentDiaryAtom);
  const [, fetchDiary] = useAtom(fetchDiaryByDateAtom);
  const [, upsertDiary] = useAtom(upsertDiaryAtom);
  const [, deleteDiary] = useAtom(deleteDiaryAtom);
  const [isLoading] = useAtom(isDiaryLoadingAtom);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // 背景和边框颜色
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  
  // 当日期变化时加载日记
  useEffect(() => {
    fetchDiary(date);
  }, [date, fetchDiary]);
  
  // 当加载的日记变化时，更新编辑框内容
  useEffect(() => {
    setContent(currentDiary?.content || '');
  }, [currentDiary]);
  
  // 保存日记
  const handleSave = async () => {
    if (!content.trim()) {
      toast({
        title: t('v0/calendar:diary.emptyContentWarning'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsSaving(true);
    try {
      const formattedDate = format(date, 'yyyy-MM-dd');
      await upsertDiary({
        date: formattedDate,
        content: content.trim(),
      });
      
      toast({
        title: t('v0/calendar:diary.savedSuccessfully'),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('保存日记失败:', error);
      toast({
        title: t('v0/calendar:diary.saveFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // 删除日记
  const handleDelete = async () => {
    if (!currentDiary) return;
    
    setIsDeleting(true);
    try {
      const success = await deleteDiary(date);
      if (success) {
        setContent('');
        toast({
          title: t('v0/calendar:diary.deletedSuccessfully'),
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        throw new Error('删除失败');
      }
    } catch (error) {
      console.error('删除日记失败:', error);
      toast({
        title: t('v0/calendar:diary.deleteFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // 格式化日期用于展示
  const formattedDate = format(date, 'yyyy年MM月dd日');
  
  return (
    <Box
      bg={bg}
      borderRadius="xl"
      shadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
      p={4}
      h="100%"
    >
      <VStack spacing={4} align="stretch" h="100%">
        <Flex justify="space-between" align="center">
          <Heading size="md">{t('v0/calendar:diary.title')}</Heading>
          <Flex>
            <Button
              leftIcon={<FiSave />}
              colorScheme="blue"
              size="sm"
              onClick={handleSave}
              isLoading={isSaving}
              mr={2}
            >
              {t('v0/common:actions.save')}
            </Button>
            {currentDiary && (
              <IconButton
                icon={<FiTrash2 />}
                colorScheme="red"
                size="sm"
                aria-label={t('v0/common:actions.delete')}
                onClick={handleDelete}
                isLoading={isDeleting}
              />
            )}
          </Flex>
        </Flex>
        
        {isLoading ? (
          <Flex justify="center" align="center" flex="1">
            <Spinner size="lg" />
          </Flex>
        ) : (
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('v0/calendar:diary.placeholder')}
            size="md"
            resize="none"
            flex="1"
            minH="200px"
            h="calc(100% - 40px)"
          />
        )}
      </VStack>
    </Box>
  );
}; 